/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import type { CreateClientUserManagementRoutesOptions } from '@/features/client-user-management/routes'
import type { ClientUserDetailsViewModel } from '@/features/client-user-management/ClientUserDetails/ClientUserDetailsViewModel'
import { Id } from '@/infra/id'
import { links } from '@/utils/link'

/**
 * Data returned by {@link createClientUserDetailsViewLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type ClientUserDetailsViewLoaderData = {
  vm: ClientUserDetailsViewModel
} | null

/**
 * Creates a loader function for the client details view that provides the
 * view model for the route's client, redirects users who may not see it, and
 * redirects to the client list when the client doesn't exist.
 *
 * @param options
 */
export function createClientUserDetailsViewLoader(
  options: CreateClientUserManagementRoutesOptions,
): LoaderFunction {
  return async ({
    params,
    request: { signal },
  }): Promise<ClientUserDetailsViewLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (!workspace) {
      return null
    }

    const internal = await options.clientUserManagement.internal()
    const vm = await internal.acquireClientUserDetailsViewModel(
      Id(params.userPublicId),
      signal,
    )
    if (!vm.canAccessUserDetails) {
      throw redirect(links.welcome())
    }

    if (!vm.hasMember) {
      // The view renders a failed load from the task's own state.
      await vm.fetch(signal).catch(() => undefined)
      if (vm.fetch.state === 'resolved' && !vm.hasMember) {
        throw redirect(links.clientUserManagement())
      }
    }

    return { vm }
  }
}
